import { useEffect, useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router";
import { AUA, Constant, Track, UserScheme } from "client-v2";
import ReactPixel from "react-facebook-pixel";
import { useDispatch } from "react-redux";
import { actionsCreators } from "../redux";
import { bindActionCreators } from "redux";
import Translation from "../assets/translation/index";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import GLPN from "google-libphonenumber";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";

import RegisterLayout from "./RegisterLayout";
import PaycardLayout from "./PaycardLayout";
import SuccessCardView from "../views/SuccessCardView";
import DonationLayout from "./DonationLayout";
import LetterLayout from "./LetterLayout";
import EventLayout from "./EventLayout";
import ChallengeLayout from "./ChallengeLayout";
import BirthdayLayout from "./BirthdayLayout";
import SBLayout from "./SBLayout";
import SignatureLayout from "./SignatureLayout";
import FeedLayout from "./FeedLayout";
import IbanLayout from "./IbanLayout";
import ProfileLayout from "./ProfileLayout";
import Loading from "../components/modal/Loading";
import { GoogleMapsProvider } from "./GoogleMapsContext";
import AdviceLayout from "./AdviceLayout";
import PackLayout from "./PackLayout";
import ShopLayout from "./ShopLayout";
import FelicitacionLayout from "./FelicitacionLayout";

// Create a GrowthBook instance
const growthbook = new GrowthBook({
	apiHost: "https://cdn.growthbook.io",
	clientKey: "sdk-QsRr9klLcVak4lj",
	enableDevMode: true,
	trackingCallback: (experiment, result) => {
		Track.event({
			event: "$experiment_started",
			params: {
				"Experiment name": experiment.key,
				"Variant name": result.variationId,
				$source: "growthbook",
			},
		});
	},
});

const Init = (props: Props) => {
	const location = useLocation();
	const phoneUtil = GLPN.PhoneNumberUtil.getInstance();
	const dispatch = useDispatch();
	const {
		setCurrentUser,
		setIsMobile,
		setIsScreen01,
		setIsScreen02,
		setAppTranslation,
		setAppLocation,
		setConfig,
	} = bindActionCreators(actionsCreators, dispatch);

	const [loading, setLoading] = useState(true);
	const [stripePromise, setStripePromise] = useState<
		Promise<Stripe | null> | undefined
	>(undefined);

	useEffect(() => {
		initServer();

		//Add listeners
		window.addEventListener("resize", documentProperties);
		documentProperties();
		return () => {
			window.removeEventListener("resize", documentProperties);
		};
	}, []);

	useEffect(() => {
		Track.pageView({
			path: location.pathname,
		});
	}, [location]);

	const documentProperties = () => {
		const doc = document.documentElement;
		doc.style.setProperty("--doc-height", `${window.innerHeight}px`);
		doc.style.setProperty("--doc-width", `${window.innerWidth}px`);
		doc.style.setProperty("--nav-bar-height", `72px`);
		doc.style.setProperty("--nav-bar-height-mobile", `62px`);
		if (window.screen.width <= 1350) setIsScreen01(true);
		else setIsScreen01(false);
		if (window.screen.width <= 1050) setIsScreen02(true);
		else setIsScreen02(false);
		if (window.screen.width <= 768) setIsMobile(true);
		else setIsMobile(false);
	};

	const initServer = async () => {
		//Get UTMs
		const params = new URLSearchParams(window.location.search);
		const utmData = {
			source: params.get("utm_source"),
			medium: params.get("utm_medium"),
			campaign: params.get("utm_campaign"),
			term: params.get("utm_term"),
			content: params.get("utm_content"),
		};

		//Init server
		const env: any = process.env.REACT_APP_ENV
			? process.env.REACT_APP_ENV
			: process.env.NODE_ENV;
		const result = await AUA.initServer({
			env: env,
			localhost: process.env.REACT_APP_LOCALHOST ? true : false,
			utmData: {
				source: utmData.source ? utmData.source : undefined,
				medium: utmData.medium ? utmData.medium : undefined,
				campaign: utmData.campaign ? utmData.campaign : undefined,
				term: utmData.term ? utmData.term : undefined,
				content: utmData.content ? utmData.content : undefined,
			},
		});

		if (result.data) {
			setCurrentUser(result.data);
		}

		//Init Firebase
		const firebaseConfig =
			env === "production"
				? {
						apiKey: "AIzaSyAnbEKh_CfTcrRT381ihWbkhO2Qb_ovGhk",
						authDomain: "app-reactnative-df6be.firebaseapp.com",
						projectId: "app-reactnative-df6be",
						storageBucket:
							"app-reactnative-df6be.firebasestorage.app",
						messagingSenderId: "380280227864",
						appId: "1:380280227864:web:621b13d55d7d9b4a6d1b6d",
						measurementId: "G-K56WYZ9F6B",
				  }
				: {
						apiKey: "AIzaSyDMg6TVcskhZWtC9hCwXiMrDWN1v233HVs",
						authDomain: "app-reactnative-dev.firebaseapp.com",
						projectId: "app-reactnative-dev",
						storageBucket:
							"app-reactnative-dev.firebasestorage.app",
						messagingSenderId: "692490730533",
						appId: "1:692490730533:web:1e856848b05b2656d913cc",
						measurementId: "G-RZW48GZV3W",
				  };

		const app = initializeApp(firebaseConfig);
		const analytics = getAnalytics(app);

		//Init Stripe
		const stripeKey = AUA.getStripeKey();
		const stripePromise = loadStripe(stripeKey);
		setStripePromise(stripePromise);

		//Init facebook
		ReactPixel.init("2520818358062687", undefined, {
			autoConfig: true,
			debug: false,
		});
		ReactPixel.pageView();

		//Init language and translation
		const language = initLanguage(result.data);

		//Init Config
		const config: any = await AUA.getConfig();
		setConfig(config);

		//Init Navigation Properties
		const navigationProperties = localStorage.getItem(
			"navigationProperties"
		);
		const navigationPropertiesObj = navigationProperties
			? JSON.parse(navigationProperties)
			: {};
		const isVerified =
			navigationPropertiesObj && navigationPropertiesObj.currency
				? navigationPropertiesObj.currency.isVerified
				: false;

		const currency = isVerified
			? navigationPropertiesObj.currency
			: await initCurrency(
					language.countryCode,
					language.language,
					config
			  );

		//TODO
		// const currency = await initCurrency("es", "es");

		//Set App Location
		const tempAppLocation = {
			language: language.language,
			country_code: language.countryCode,
			currency: currency
				? currency
				: {
						name: "eur",
						symbol: "€",
						hasSEPA: true,
						isVerified: false,
						conversion: 1,
						price: {
							annually: {
								min: 50,
								max: 250,
								default: 120,
								gift: 200,
								jump: 10,
							},
							monthly: {
								min: 5,
								max: 40,
								default: 15,
								gift: 20,
								jump: 1,
							},
							one: {
								min: 5,
								max: 500,
								default: 50,
								jump: 5,
							},
						},
				  },
		};
		localStorage.setItem(
			"navigationProperties",
			JSON.stringify(tempAppLocation)
		);
		setAppLocation(tempAppLocation);

		//Init test a/b
		await initTestAB(result.data);

		setLoading(false);
	};

	const initLanguage = (
		user?: UserScheme
	): {
		language: string;
		countryCode: string;
	} => {
		//Get user language
		const navigationProperties = localStorage.getItem(
			"navigationProperties"
		);
		const navigationPropertiesObj = navigationProperties
			? JSON.parse(navigationProperties)
			: undefined;
		const tempLanguage = navigator.language ? navigator.language : "es-ES";
		const language =
			navigationPropertiesObj && navigationPropertiesObj.language
				? navigationPropertiesObj.language
				: tempLanguage.split("-")[0];

		//Get country code from phone number or from navigator
		const phone = user ? user.phone : undefined;
		let country_code = "ES";
		if (phone) {
			const result = phoneUtil.parse(phone);
			const phoneCountryCode = result.getCountryCode();
			if (phoneCountryCode) {
				const contryContant = Constant.countries().find(
					(i) => i.prefix === "+" + phoneCountryCode.toString()
				);
				if (contryContant) {
					country_code = contryContant.countryCode;
				}
			}
		} else {
			const tempLanguage = navigator.language
				? navigator.language
				: "es-ES";
			country_code = tempLanguage.split("-")[1];
		}

		//Init translation
		const translation = (Translation as any)[language]
			? (Translation as any)[language]
			: Translation["es"];
		setAppTranslation(translation);

		return {
			language: language,
			countryCode: country_code,
		};
	};

	const initCurrency = async (
		countryCode: string,
		language: string,
		config: any
	): Promise<any> => {
		const response = await fetch(
			`https://restcountries.com/v3.1/alpha/${countryCode}`,
			{ signal: AbortSignal.timeout(3000) }
		).catch(() => {
			return null;
		});

		const result = await response?.json();
		const currency =
			result && result[0] && result[0].currencies
				? {
						name: Object.keys(result[0].currencies)[0],
						symbol: result[0].currencies[
							Object.keys(result[0].currencies)[0]
						].symbol,
				  }
				: {
						name: "eur",
						symbol: "€",
				  };
		//Check if country has a valid currency
		const validCurrencies = config
			? config.attributes.VALID_CURRENCIES
			: undefined;
		const isValidCurrency = validCurrencies
			? validCurrencies.find((i: any) => i.name === currency.name)
			: undefined;

		if (isValidCurrency) {
			return {
				...isValidCurrency,
				symbol: new Intl.NumberFormat(language, {
					style: "currency",
					currency: currency.name,
				})
					.formatToParts(1)
					.find((part) => part.type === "currency")?.value,
				name: currency.name.toLocaleLowerCase(),
				isVerified: true,
				hasSEPA:
					currency.name.toLocaleLowerCase() === "eur" ? true : false,
			};
		} else {
			return undefined;
		}
	};

	const initTestAB = async (user?: UserScheme) => {
		// Set user attributes for targeting (from cookie, auth system, etc.)
		const mixpanelId = await Track.getDistinctId();
		growthbook.init({ streaming: true });
		growthbook.setAttributes({
			id: mixpanelId,
			//deviceId: ip.ip,
			company: "aua",
			loggedIn: user ? true : false,
			employee:
				(process.env.REACT_APP_ENV
					? process.env.REACT_APP_ENV
					: process.env.NODE_ENV) === "development",
			url: window.location.href,
		});
	};

	return loading || !stripePromise ? (
		<Loading isVisible={loading ? true : false} />
	) : (
		<GoogleMapsProvider>
			<Elements stripe={stripePromise}>
				<GrowthBookProvider growthbook={growthbook}>
					<Routes>
						<Route
							path="/registro/*"
							element={<RegisterLayout />}
						/>
						<Route path="/sb/*" element={<SBLayout />} />
						<Route
							path="/donacion/*"
							element={<DonationLayout />}
						/>
						<Route
							path="/proceso-donacion/*"
							element={<DonationLayout processOnly={true} />}
						/>
						<Route path="/consejo/*" element={<AdviceLayout />} />
						<Route path="/tarjeta/*" element={<PaycardLayout />} />
						<Route path="/iban/*" element={<IbanLayout />} />

						<Route path="/cartas/*" element={<LetterLayout />} />
						<Route path="/evento/*" element={<EventLayout />} />
						<Route path="/reto/*" element={<ChallengeLayout />} />
						<Route
							path="/cumpleanios/*"
							element={<BirthdayLayout />}
						/>
						<Route
							path="/felicitacion/*"
							element={<FelicitacionLayout />}
						/>
						<Route
							path="/card-success/*"
							element={<SuccessCardView />}
						/>
						<Route path="/firma/*" element={<SignatureLayout />} />
						<Route path="/feed/*" element={<FeedLayout />} />
						<Route path="/pack/*" element={<PackLayout />} />
						<Route path="/shop/*" element={<ShopLayout />} />
						<Route path="/perfil/*" element={<ProfileLayout />} />
						<Route
							path="/"
							element={
								<Navigate
									to={`/${
										process.env.APP_PATH
											? process.env.APP_PATH
											: "cartas"
									}`}
									replace
								/>
							}
						/>
					</Routes>
				</GrowthBookProvider>
			</Elements>
		</GoogleMapsProvider>
	);
};
export default Init;
export interface Props {}
